import { setToastMessage } from 'src/store/my_pages/app';
import store, { dispatch } from 'src/store/Store';
import { apiConfig } from 'src/config/apiConfig';
import axios, { AxiosHeaders } from 'axios';
import { isTokenExpired, shouldRefreshToken } from 'src/utils/utils';
import { publicRoutes } from 'src/routes/Router';
import { attemptTokenRefresh, clearAllTokens, hasAnyTokens, getAuthTokens } from 'src/utils/tokenRefresh';

/* 
When it runs => On every single API request made by the application
Purpose => Ensures API calls have valid tokens and handles token refresh during API operations
Scope => Request-level authentication
Frequency => Runs on every API call (which could be hundreds per session)
*/
const axiosIn = axios.create({
  baseURL: apiConfig.API_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json', // default
    // 'Content-Encoding': 'zstd', // default
    'Cache-Control': 'public, max-age=600',
    // 'Accept-Encoding': 'br, gzip, zstd, zip, deflate', // => This is not allowed. Do it on ss. Support browsers will handle automatically.
  },
  // withCredentials: true,
});

//? To parse cookies (since useCookies cannot be used here)
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts: any = value.split(`; ${name}=`);
  if (parts.length === 2) return parts?.pop().split(';').shift();
};


axiosIn.interceptors.request.use(
  async (config) => {
    try {
      // Detect if current path is a public route. We still attach Authorization if available
      // to support transitions (e.g. after 2FA verify) that may fire requests before redirect.
      const currentPath = window.location.pathname;
      const isOnPublicRoute = publicRoutes.some((route) => currentPath === route.path);

      const state = store.getState();
      let authToken = state.authReducer.authToken;

      // Get all tokens from localStorage using centralized helper
      const { accessToken, refreshToken, accessTokenExpiry, refreshTokenExpiry } = getAuthTokens();

      // If Redux is missing the token, try to get it from localStorage
      if (!authToken && accessToken) {
        authToken = accessToken;
        dispatch({ type: 'authReducer/setAuthAccessToken', payload: authToken });
      }

      // Check if access token is expired or needs refresh
      const isAccessTokenExpired = isTokenExpired(accessTokenExpiry);
      const shouldRefresh = shouldRefreshToken(accessTokenExpiry, refreshTokenExpiry);

      // If we have no tokens at all (fresh login), don't try to refresh
      const hasNoTokens = !authToken && !accessTokenExpiry && !refreshTokenExpiry;

      // If access token is expired or should be refreshed, try to refresh
      if (!hasNoTokens && (!authToken || isAccessTokenExpired || shouldRefresh) && refreshToken && refreshTokenExpiry && !isTokenExpired(refreshTokenExpiry)) {
        try {
          const refreshResult = await attemptTokenRefresh(refreshToken);
          if (refreshResult.success && refreshResult.newAccessToken) {
            authToken = refreshResult.newAccessToken;
          } else {
            // Refresh failed, clear tokens and redirect
            clearAllTokens();
            if (!window.location.pathname.includes('/auth/login')) {
              window.location.href = '/auth/login';
            }
            setTimeout(() => {
              dispatch(setToastMessage({ msg: 'Token expired, please login again', iconKey: 'error', color: 'warning', duration: 5000 }));
            }, 100);
            return Promise.reject(new Error('Unable to refresh access token'));
          }
        } catch (refreshError) {
          clearAllTokens();
          if (!window.location.pathname.includes('/auth/login')) {
            window.location.href = '/auth/login';
          }
          setTimeout(() => {
            dispatch(setToastMessage({ msg: 'Token expired, please login again', iconKey: 'error', color: 'warning', duration: 5000 }));
          }, 100);
          return Promise.reject(refreshError);
        }
      }

      // If access token is still expired and no refresh token available, redirect to login
      // Only show session expired message if there was actually a session (tokens existed)
      // Don't trigger this for fresh logins (no tokens at all)
      if (!hasNoTokens && isAccessTokenExpired && (!refreshTokenExpiry || isTokenExpired(refreshTokenExpiry))) {
        const hadTokens = hasAnyTokens();

        clearAllTokens();

        if (!window.location.pathname.includes('/auth/login')) {
          window.location.href = '/auth/login';
        }

        // Only show session expired message if there were actually tokens that expired
        if (hadTokens) {
          setTimeout(() => {
            dispatch(setToastMessage({ msg: 'Session expired, please login again', iconKey: 'error', color: 'warning', duration: 5000 }));
          }, 100);
        }
        return Promise.reject(new Error('Access token expired and no valid refresh token'));
      }

      if (!config.headers) {
        config.headers = AxiosHeaders.from({});
      }
      if (authToken) {
        config.headers['Authorization'] = `Bearer ${authToken}`;
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => {
    //? Handle the error before the request is sent
    return Promise.reject(error);
  },
);

//? Optional: Add a response interceptor
axiosIn.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorData = error?.response?.data;
    const errorStatus = errorData?.status;
    const httpStatus = error?.response?.status;
    const errorMessage = errorData?.message;
    const isOnLoginPage = window.location.pathname.includes('/auth/login');
    const originalRequest = error.config;

    // If we get a 401 or 'Token is expired or invalid', always try refresh if refresh token exists
    if (((httpStatus === 401) || (errorStatus === 401) || errorMessage === 'Token is expired or invalid') && !originalRequest._retry) {
      const { refreshToken } = getAuthTokens();
      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const refreshResult = await attemptTokenRefresh(refreshToken);
          // If refresh endpoint returns a new access token
          if (refreshResult.success && refreshResult.newAccessToken) {
            // Ensure Authorization header is set for the retried request
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${refreshResult.newAccessToken}`,
            };
            return axiosIn(originalRequest);
          }
        } catch (refreshError) {
          // If refresh fails (e.g., 401), clear tokens and redirect
          const hadTokens = hasAnyTokens();

          clearAllTokens();

          if (!isOnLoginPage) {
            window.location.href = '/auth/login';
          }

          // Only show session expired message if there were actually tokens that expired
          if (hadTokens) {
            setTimeout(() => {
              dispatch(setToastMessage({ msg: 'Token expired, please login again', iconKey: 'error', color: 'warning', duration: 5000 }));
            }, 100);
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available, force logout
        const hadTokens = hasAnyTokens();

        clearAllTokens();

        if (!isOnLoginPage) {
          window.location.href = '/auth/login';
        }

        // Only show session expired message if there were actually tokens that expired
        if (hadTokens) {
          setTimeout(() => {
            dispatch(setToastMessage({ msg: 'Token expired, please login again', iconKey: 'error', color: 'warning', duration: 5000 }));
          }, 100);
        }
      }
    }
    // For all other errors, just reject
    return Promise.reject(error);
  },
);

export default axiosIn;
